import { extractDisplayName, extractTokenFromHtml } from "./html-parsing";
import { EXTERNAL_URLS } from "@/shared/constants";

const defaultHeaders = {
  "User-Agent":
    "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36",
  Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8",
  "Accept-Language": "en-US,en;q=0.9,tr;q=0.8",
  DNT: "1",
};

// Fetch wrapper with cookie support using Bun.CookieMap
export async function fetchWithCookies(
  url: string,
  cookieMap: Bun.CookieMap,
  options: RequestInit & { redirectCount?: number } = {},
): Promise<{ response: Response; updatedCookies: Bun.CookieMap }> {
  const cookieEntries = Array.from(cookieMap.entries());
  const cookieHeader = cookieEntries.map(([name, value]) => `${name}=${value}`).join("; ");

  const headers: Record<string, string> = {
    ...defaultHeaders,
    ...(options.headers as Record<string, string>),
  };

  if (cookieHeader) {
    headers.Cookie = cookieHeader;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    redirect: "manual", // Handle redirects manually to capture cookies
  });

  // Create updated cookie map from existing cookies
  const updatedCookies = new Bun.CookieMap(cookieMap.toJSON());

  // Store cookies from response
  const setCookieHeaders = response.headers.getSetCookie?.() || [];
  if (setCookieHeaders.length > 0) {
    setCookieHeaders.forEach((setCookieHeader) => {
      try {
        const cookie = Bun.Cookie.parse(setCookieHeader);
        if (!cookie.isExpired()) {
          updatedCookies.set(cookie);
        }
      } catch (e) {
        console.error("Failed to parse cookie:", setCookieHeader, e);
      }
    });
  }

  // Follow redirects manually (up to 5)
  if (response.status >= 300 && response.status < 400 && response.headers.get("location")) {
    const redirectCount = options.redirectCount || 0;
    if (redirectCount < 5) {
      const location = response.headers.get("location");
      const redirectUrl = location!.startsWith("http") ? location! : new URL(location!, url).href;

      // Prepare headers for redirect, removing Content-Type and setting proper referrer
      const redirectHeaders = { ...options.headers } as Record<string, string>;
      delete redirectHeaders["Content-Type"];
      redirectHeaders.Referer = response.url;

      return fetchWithCookies(redirectUrl, updatedCookies, {
        method: "GET",
        body: undefined,
        headers: redirectHeaders,
        redirectCount: redirectCount + 1,
      });
    }
  }

  // Clone the response to avoid ReadableStream locked errors
  return { response: response.clone(), updatedCookies };
}

export async function getWelfareToken(cookieMap: Bun.CookieMap) {
  const request = await fetchWithCookies(EXTERNAL_URLS.WELFARE_TOKEN, cookieMap);

  if (request.response.url !== EXTERNAL_URLS.WELFARE_TOKEN) {
    return { success: false, sessionExpired: true, token: null };
  }

  // https://login.360welfare.it/utils/generaImgCodiceBarre.php?c=XXXXXX|XXXXXX
  // parse token from url
  const text = await request.response.text();
  const token = new URL(text).searchParams.get("c")?.split("|");

  if (token?.length === 2) {
    return { success: true, sessionExpired: false, token: token[1] };
  } else {
    console.error(`Welfare token generation failed, received: ${text}`);
    return { success: false, sessionExpired: false, token: null };
  }
}

export async function checkWelfareSession(welfareCookies: Bun.CookieMap): Promise<boolean> {
  const request = await fetchWithCookies(EXTERNAL_URLS.WELFARE_DASHBOARD, welfareCookies);
  return request.response.url === EXTERNAL_URLS.WELFARE_DASHBOARD;
}

export async function getWelfareSession(polimiCookies: Bun.CookieMap): Promise<Bun.CookieMap | null> {
  const welfareAuthParams = new URLSearchParams({
    response_type: "code",
    client_id: "3137-welfare.dsu",
    redirect_uri: "https://login.360welfare.it/politecnico_return_uri.php",
    scope: "openid",
    state: "prova",
    access_type: "offline",
  });

  const welfareLoginResult = await fetchWithCookies("https://oauthidp.polimi.it/oauthidp/oauth2/auth", polimiCookies, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Origin: "https://login.360welfare.it",
      Referer: "https://login.360welfare.it/",
    },
    body: welfareAuthParams.toString(),
  });

  const pj1param = new URL(welfareLoginResult.response.url).searchParams.get("__pj1") || "";

  // New cookie for step 2
  const ingressCookie = welfareLoginResult.updatedCookies.get("INGRESSCOOKIE");
  if (ingressCookie) {
    polimiCookies.set("INGRESSCOOKIE", ingressCookie);
  }

  const STEP1_URL =
    "https://aunicalogin.polimi.it/aunicalogin/aunicalogin.jsp?lang=IT&id_servizio=3137&id_servizio_idp=828";
  if (welfareLoginResult.response.url.startsWith(STEP1_URL)) {
    // Extract required cookies from polimiCookies
    const ssoLogin = polimiCookies?.get("SSO_LOGIN") || "";
    const matricolaScelta = polimiCookies?.get("MATRICOLA_SCELTA") || "";
    const restaConnesso = polimiCookies?.get("RESTA_CONNESSO") || "";

    const welfareStep2Params = new URLSearchParams({
      SSO_LOGIN: ssoLogin,
      MATRICOLA_SCELTA: matricolaScelta,
      COOKIE_HCSS: "",
      impersonificato: "",
      RESTA_CONNESSO: restaConnesso,
      polij_device_category: "DESKTOP",
    });

    const welfareLoginResult2 = await fetchWithCookies(
      EXTERNAL_URLS.POLIMI_WELFARE_STEP2 + `&__pj1=${pj1param}`,
      polimiCookies,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Origin: "https://aunicalogin.polimi.it",
          Referer: welfareLoginResult.response.url,
        },
        body: welfareStep2Params.toString(),
      },
    );

    if (welfareLoginResult2.response.url === EXTERNAL_URLS.WELFARE_DASHBOARD) {
      const welfareCookies = new Bun.CookieMap();
      const phpSessionId = welfareLoginResult2.updatedCookies.get("PHPSESSID") || "";
      const loadBalancerCookie = welfareLoginResult2.updatedCookies.get("ARRAffinity") || "";
      welfareCookies.set("PHPSESSID", phpSessionId);
      welfareCookies.set("ARRAffinity", loadBalancerCookie);
      return welfareCookies;
    }
  }
  return null;
}

// API client wrapper
export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = "") {
    this.baseUrl = baseUrl;
  }

  async get<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "GET",
      credentials: "include",
      ...options,
    });

    if (!response.ok) {
      // Try to get error message from response body
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // If we can't parse JSON, keep the default error message
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async post<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      credentials: "include",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    if (!response.ok) {
      // Try to get error message from response body
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // If we can't parse JSON, keep the default error message
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async put<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      credentials: "include",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    if (!response.ok) {
      // Try to get error message from response body
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // If we can't parse JSON, keep the default error message
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async delete<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: "DELETE",
      credentials: "include",
      ...options,
    });

    if (!response.ok) {
      // Try to get error message from response body
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // If we can't parse JSON, keep the default error message
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

export const apiClient = new ApiClient();
